import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

interface BriefMeta {
  id: string;
  title: string;
  password_required: boolean;
}

type PageState =
  | { stage: 'loading' }
  | { stage: 'gate'; meta: BriefMeta; error?: string }
  | { stage: 'open'; meta: BriefMeta; error?: string }
  | { stage: 'fetching' }
  | { stage: 'ready'; html: string }
  | { stage: 'error'; message: string };

function apiBase(): string {
  // In dev the vite proxy rewrites /api → backend; in prod they're co-located.
  return '';
}

async function fetchMeta(briefId: string): Promise<BriefMeta> {
  const res = await fetch(`${apiBase()}/api/public/briefs/${briefId}`);
  if (!res.ok) throw new Error('Brief not found');
  return res.json();
}

async function verifyPassword(briefId: string, password: string): Promise<string> {
  const res = await fetch(`${apiBase()}/api/public/briefs/${briefId}/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) throw new Error('Incorrect password');
  if (!res.ok) throw new Error('Verification failed');
  const data = await res.json();
  return data.token as string;
}

async function fetchHtml(briefId: string, token: string): Promise<string> {
  const res = await fetch(`${apiBase()}/api/public/briefs/${briefId}/html?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error('Failed to load brief');
  const data = await res.json();
  return data.content as string;
}

function storageKey(briefId: string) {
  return `brief_token_${briefId}`;
}

export function PublicBriefPage() {
  const { briefId } = useParams<{ briefId: string }>();
  const [state, setState] = useState<PageState>({ stage: 'loading' });
  const [password, setPassword] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!briefId) {
      setState({ stage: 'error', message: 'Invalid brief link' });
      return;
    }
    init(briefId);
  }, [briefId]);

  // Inject HTML into iframe when content is ready
  useEffect(() => {
    if (state.stage !== 'ready' || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(state.html);
    doc.close();
  }, [state]);

  async function init(id: string) {
    try {
      const meta = await fetchMeta(id);

      // Check for a saved token
      const savedToken = sessionStorage.getItem(storageKey(id));
      if (savedToken) {
        await loadBrief(id, savedToken, meta);
        return;
      }

      if (!meta.password_required) {
        // No password — get a token automatically
        const token = await verifyPassword(id, '');
        sessionStorage.setItem(storageKey(id), token);
        await loadBrief(id, token, meta);
      } else {
        setState({ stage: 'gate', meta });
      }
    } catch (err) {
      setState({ stage: 'error', message: (err as Error).message || 'Brief not found' });
    }
  }

  async function loadBrief(id: string, token: string, meta: BriefMeta) {
    setState({ stage: 'fetching' });
    try {
      const html = await fetchHtml(id, token);
      setState({ stage: 'ready', html });
    } catch {
      sessionStorage.removeItem(storageKey(id));
      setState({ stage: 'gate', meta, error: 'Session expired. Please enter the password again.' });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state.stage !== 'gate' || !briefId) return;
    const meta = state.meta;
    setState({ stage: 'open', meta });
    try {
      const token = await verifyPassword(briefId, password);
      sessionStorage.setItem(storageKey(briefId), token);
      await loadBrief(briefId, token, meta);
    } catch (err) {
      setState({ stage: 'gate', meta, error: (err as Error).message || 'Incorrect password' });
    }
  }

  // -------------------------------------------------------------------------
  // Render states
  // -------------------------------------------------------------------------

  if (state.stage === 'loading' || state.stage === 'fetching') {
    return (
      <div className="pb-shell pb-shell--center">
        <div className="pb-spinner" />
      </div>
    );
  }

  if (state.stage === 'error') {
    return (
      <div className="pb-shell pb-shell--center">
        <div className="pb-gate">
          <div className="pb-gate__logo">TC</div>
          <h1 className="pb-gate__title">Brief not available</h1>
          <p className="pb-gate__hint">{state.message}</p>
        </div>
      </div>
    );
  }

  if (state.stage === 'gate' || state.stage === 'open') {
    const { meta, error } = state;
    const submitting = state.stage === 'open';
    return (
      <div className="pb-shell pb-shell--center">
        <form className="pb-gate" onSubmit={handleSubmit}>
          <div className="pb-gate__logo">TC</div>
          <h1 className="pb-gate__title">{meta.title}</h1>
          <p className="pb-gate__hint">This brief is password-protected.</p>
          <input
            className="pb-gate__input"
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
            disabled={submitting}
          />
          {error && <p className="pb-gate__error">{error}</p>}
          <button
            className="pb-gate__btn"
            type="submit"
            disabled={submitting || !password}
          >
            {submitting ? 'Verifying…' : 'View brief'}
          </button>
        </form>
      </div>
    );
  }

  // stage === 'ready'
  return (
    <iframe
      ref={iframeRef}
      title="Affiliate Brief"
      className="pb-frame"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
}
